import { useState } from "react";
import type { Objective, RagScore } from "../data/types";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const SHORT_MONTHS_UC = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
// Calendar-month indices in financial-year order: Apr (3) ... Mar (2).
const FY_MONTH_ORDER: number[] = [3, 4, 5, 6, 7, 8, 9, 10, 11, 0, 1, 2];
const CURRENT_MONTH_IDX = new Date().getMonth();

/** Two-digit calendar year for a given calendar-month index within the supplied reporting year. */
function shortYearLabel(calMonthIdx: number, reportingYear: number): string {
  const cellYear = calMonthIdx >= 3 ? reportingYear : reportingYear + 1;
  return String(cellYear % 100).padStart(2, "0");
}

export type Rag = "G" | "A" | "R" | null;

/* ---------- helpers ---------- */

export function ragLabel(r: Rag): string {
  if (r === "G") return "Green";
  if (r === "A") return "Amber";
  if (r === "R") return "Red";
  return "Not set";
}

function ragPillClass(r: Rag): string {
  if (r === "G") return "bg-rag-green-bg text-rag-green";
  if (r === "A") return "bg-rag-amber-bg text-rag-amber-ink";
  if (r === "R") return "bg-rag-red-bg text-rag-red";
  return "bg-rag-unset-bg text-ink-3";
}

function ragDotClass(r: Rag): string {
  if (r === "G") return "bg-rag-green";
  if (r === "A") return "bg-rag-amber";
  if (r === "R") return "bg-rag-red";
  return "bg-rag-unset";
}

function ragCellClass(r: Rag): string {
  if (r === "G") return "bg-rag-green";
  if (r === "A") return "bg-rag-amber";
  if (r === "R") return "bg-rag-red";
  return "bg-rag-unset opacity-40";
}

/* ---------- ObjectiveCard ----------
 *
 * The 12-cell strip is rendered in financial-year order (Apr - Mar) with the
 * calendar year shown beneath each cell. Clicking a cell selects that month
 * and surfaces its narrative in a stable-height panel below the strip; the
 * card's onClick (which opens the edit modal when editable) is suppressed
 * via stopPropagation so cell clicks only ever change the narrative view.
 *
 * Default selected month: today's calendar month when this card is showing
 * the current FY; April (start of FY) otherwise. The narrative panel keeps
 * its height even when no narrative is recorded so the card layout doesn't
 * jump as the user clicks between months.
 */

export function ObjectiveCard({
  objective,
  rag,
  scores,
  reportingYear,
  editable,
  onClick,
}: {
  objective: Objective;
  rag: Rag;
  /** 12 entries indexed by calendar month (0=Jan ... 11=Dec). */
  scores: (RagScore | null)[];
  reportingYear: number;
  editable: boolean;
  onClick?: () => void;
}) {
  const isViewingCurrentFY = reportingYear === reportingYearForDate();
  const [selectedCalMonth, setSelectedCalMonth] = useState<number>(() =>
    isViewingCurrentFY ? CURRENT_MONTH_IDX : 3,
  );

  const selectedScore = scores[selectedCalMonth] ?? null;
  const selectedNarrative = selectedScore?.narrative ?? null;
  const selectedYearShort = shortYearLabel(selectedCalMonth, reportingYear);

  return (
    <article
      onClick={editable ? onClick : undefined}
      className={[
        "group relative overflow-hidden rounded-lg border border-rule bg-card px-[22px] pt-5 pb-[18px] transition",
        editable
          ? "cursor-pointer hover:-translate-y-px hover:border-rule-strong hover:shadow-card"
          : "",
      ].join(" ")}
    >
      {/* header: category + RAG pill */}
      <div className="mb-[10px] flex items-start justify-between gap-3">
        <div className="font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-brass">
          {objective.category ?? "Uncategorised"}
        </div>
        <div
          className={[
            "inline-flex items-center gap-[7px] rounded-full px-[11px] py-1 text-[12px] font-medium",
            ragPillClass(rag),
          ].join(" ")}
        >
          <span className={["h-2 w-2 rounded-full", ragDotClass(rag)].join(" ")} />
          {ragLabel(rag)}
        </div>
      </div>

      {/* title */}
      <div className="mb-2 font-display text-[17px] font-medium leading-[1.3] tracking-[-0.005em] text-ink">
        {objective.category ?? "Objective"}
      </div>

      {/* summary - clamped to 2 lines */}
      <div
        className="mb-4 overflow-hidden text-[13px] leading-[1.55] text-ink-2"
        style={{
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
        }}
      >
        {objective.objectiveText || (
          <span className="italic text-ink-3">No objective text yet.</span>
        )}
      </div>

      {/* footer: FY-ordered strip + narrative panel */}
      <div className="border-t border-dashed border-rule pt-[14px]">
        <div className="grid grid-cols-12 gap-[3px]">
          {FY_MONTH_ORDER.map((calMonth) => {
            const score = scores[calMonth] ?? null;
            const cellRag = ragFromCode(score?.ragValue ?? null);
            const isCurrent = calMonth === CURRENT_MONTH_IDX && isViewingCurrentFY;
            const isSelected = calMonth === selectedCalMonth;
            const cellYear = calMonth >= 3 ? reportingYear : reportingYear + 1;
            const ringCls = isSelected
              ? "outline outline-2 outline-offset-1 outline-nymr-navy"
              : isCurrent
                ? "outline outline-2 outline-offset-1 outline-brass"
                : "";
            return (
              <button
                key={calMonth}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedCalMonth(calMonth);
                }}
                aria-pressed={isSelected}
                title={`${MONTHS[calMonth]} ${cellYear}: ${ragLabel(cellRag)}`}
                className="flex flex-col items-stretch focus:outline-none"
              >
                <span
                  className={[
                    "h-[22px] rounded-[2px] transition",
                    ragCellClass(cellRag),
                    ringCls,
                  ].join(" ")}
                />
                <span
                  className={[
                    "mt-[3px] text-center font-mono text-[8.5px] tracking-[0.04em]",
                    isSelected ? "font-medium text-ink" : "text-ink-3",
                  ].join(" ")}
                >
                  {SHORT_MONTHS_UC[calMonth]} {shortYearLabel(calMonth, reportingYear)}
                </span>
              </button>
            );
          })}
        </div>

        {/* Narrative panel - stable height so the card doesn't reflow on click. */}
        <div className="mt-[14px] min-h-[64px] rounded-md border border-rule bg-paper-sunk px-[12px] py-[9px]">
          <div className="mb-[3px] font-mono text-[9px] uppercase tracking-[0.14em] text-ink-3">
            {SHORT_MONTHS_UC[selectedCalMonth]} {selectedYearShort} narrative
          </div>
          {selectedNarrative ? (
            <div
              className="overflow-hidden whitespace-pre-line text-[12.5px] leading-[1.5] text-ink-2"
              style={{
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
              }}
            >
              {selectedNarrative}
            </div>
          ) : (
            <div className="text-[12px] italic text-ink-3">
              No narrative recorded for this month.
            </div>
          )}
        </div>
      </div>
    </article>
  );
}

/* ---------- AddObjectiveCard ---------- */

export function AddObjectiveCard({ onClick }: { onClick: () => void }) {
  return (
    <article
      onClick={onClick}
      className="flex min-h-[200px] cursor-pointer items-center justify-center rounded-lg border border-dashed border-rule-strong bg-transparent p-6 text-ink-3 transition hover:border-brass hover:bg-card"
    >
      <div className="text-center">
        <div className="mx-auto mb-[10px] grid h-10 w-10 place-items-center rounded-full bg-[rgb(185_138_58/0.08)] text-brass">
          <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
            <path
              d="M10 4v12m-6-6h12"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
        </div>
        <div className="mb-1 font-display text-[16px] font-medium text-ink-2">
          Add an objective
        </div>
        <div className="font-mono text-[10.5px] uppercase tracking-[0.1em]">
          Choose a category &middot; draft a SMART statement
        </div>
      </div>
    </article>
  );
}

/* ---------- RagCountRing ---------- */

export function RagCountRing({
  counts,
  align = "right",
}: {
  counts: { G: number; A: number; R: number; U: number };
  align?: "left" | "right";
}) {
  return (
    <div
      className={`flex items-center gap-[10px] ${align === "right" ? "justify-end" : "justify-start"}`}
    >
      <RagCount n={counts.G} label="green" tone="text-rag-green" />
      <RagCount n={counts.A} label="amber" tone="text-rag-amber" />
      <RagCount n={counts.R} label="red" tone="text-rag-red" />
      <RagCount n={counts.U} label="not set" tone="text-ink-3" />
    </div>
  );
}

function RagCount({ n, label, tone }: { n: number; label: string; tone: string }) {
  return (
    <div className="inline-flex items-baseline gap-1">
      <span
        className={`font-display text-[22px] font-medium leading-none tracking-[-0.01em] ${tone}`}
      >
        {n}
      </span>
      <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-ink-3">{label}</span>
    </div>
  );
}

/* ---------- RAG data ---------- */

/**
 * A lookup index for RAG scores, keyed to a single reporting year.
 * Build once per page via `buildRagIndex(ragScores, reportingYear)` and
 * pass into `getCurrentRag` / `getHistory` / `countRags` / `getRagFor`.
 */
export interface RagIndex {
  reportingYear: number;
  /** Calendar month 1-12 - used as the default "current" month for pills and count rings. */
  currentMonth: number;
  /** key = `${normalisedPersonName}|${trimmedCategory}|${month}` */
  byKey: Map<string, RagScore>;
}

function normaliseName(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, " ");
}

export function ragScoreKey(personName: string, category: string, month: number): string {
  return `${normaliseName(personName)}|${category.trim()}|${month}`;
}

/**
 * Compute the NYMR reporting year for a given date.
 * Reporting year runs April-March; the integer is the calendar year it starts in.
 * Apr-Dec 2026 -> 2026.  Jan-Mar 2027 -> 2026 (still in the 2026/7 period).
 */
export function reportingYearForDate(d: Date = new Date()): number {
  return d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
}

export function buildRagIndex(scores: RagScore[], reportingYear: number): RagIndex {
  const byKey = new Map<string, RagScore>();
  for (const s of scores) {
    if (s.year !== reportingYear) continue;
    byKey.set(ragScoreKey(s.personName, s.category, s.month), s);
  }
  return {
    reportingYear,
    currentMonth: new Date().getMonth() + 1,
    byKey,
  };
}

export function ragFromCode(n: 1 | 2 | 3 | null | undefined): Rag {
  if (n === 3) return "G";
  if (n === 2) return "A";
  if (n === 1) return "R";
  return null;
}

export function ragToCode(r: Rag): 1 | 2 | 3 | null {
  if (r === "G") return 3;
  if (r === "A") return 2;
  if (r === "R") return 1;
  return null;
}

export function getCurrentRag(o: Objective, index: RagIndex): Rag {
  if (!o.category) return null;
  const s = index.byKey.get(ragScoreKey(o.personName, o.category, index.currentMonth));
  return s ? ragFromCode(s.ragValue) : null;
}

export function getHistory(o: Objective, index: RagIndex): Rag[] {
  const out: Rag[] = new Array(12).fill(null);
  if (!o.category) return out;
  for (let m = 1; m <= 12; m++) {
    const s = index.byKey.get(ragScoreKey(o.personName, o.category, m));
    if (s) out[m - 1] = ragFromCode(s.ragValue);
  }
  return out;
}

/**
 * Like getHistory but returns the full RagScore rows (not just colour codes).
 * Used by ObjectiveCard so the card can surface narratives per-month without
 * a second lookup. Indexed by calendar month: out[0]=Jan ... out[11]=Dec.
 */
export function getHistoryScores(o: Objective, index: RagIndex): (RagScore | null)[] {
  const out: (RagScore | null)[] = new Array(12).fill(null);
  if (!o.category) return out;
  for (let m = 1; m <= 12; m++) {
    const s = index.byKey.get(ragScoreKey(o.personName, o.category, m));
    if (s) out[m - 1] = s;
  }
  return out;
}

export function countRags(
  objectives: Objective[],
  index: RagIndex,
): { G: number; A: number; R: number; U: number } {
  const c = { G: 0, A: 0, R: 0, U: 0 };
  for (const o of objectives) {
    const r = getCurrentRag(o, index);
    if (r === "G") c.G++;
    else if (r === "A") c.A++;
    else if (r === "R") c.R++;
    else c.U++;
  }
  return c;
}

/** Lookup a single cell in the index - used by the team heatmap. */
export function getRagFor(
  personName: string,
  category: string,
  month: number,
  index: RagIndex,
): Rag {
  const s = index.byKey.get(ragScoreKey(personName, category, month));
  return s ? ragFromCode(s.ragValue) : null;
}

/** Lookup the full score row (for the scoring modal's existing-score check). */
export function getRagScoreFor(
  personName: string,
  category: string,
  month: number,
  index: RagIndex,
): RagScore | null {
  return index.byKey.get(ragScoreKey(personName, category, month)) ?? null;
}
